import { Button, Input } from 'antd';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';

import Loader from '../Loader';
import QuillFormik from '../utils/editor';
import FileUploader from '../utils/imageUploader';

import useCreateBlog from '@/hooks/blog/useCreateBlog';
import useUpdateBlog from '@/hooks/blog/useEditBlog';
import { Iblog, IblogDto } from '@/interfaces/entities/blog';
import { notificationService } from '@/services/notification.service';

dayjs.extend(customParseFormat);

type PropType = { editBlog: Iblog | null; setToggle: () => void };

export const CreateBlog = ({ editBlog, setToggle }: PropType) => {
	const createBlog = useCreateBlog();
	const updateBlog = useUpdateBlog();
	const [loading, setLoading] = useState<boolean>(false);

	const [initialValues, setInitialValues] = useState<IblogDto>({
		title: '',
		heading: '',
		description: '',
		date: '',
		content: '',
		img: null,
	});

	const handleSubmit = async (
		values: IblogDto,
		{ resetForm }: { resetForm: () => void },
	) => {
		setLoading(true);
		if (!editBlog) {
			const formData = new FormData();
			formData.append('title', values.title);
			formData.append('heading', values.heading);
			formData.append('description', values.description);
			formData.append('date', values.date);
			formData.append('content', values.content);

			// Handle image file
			if (values.img && values.img instanceof File) {
				formData.append('img', values.img); // actual file
			} else if (typeof values.img === 'string') {
				formData.append('img_url', values.img); // just the URL
			}

			try {
				await createBlog.mutateAsync(formData);
				resetForm();
				setToggle();
			} catch (error) {
				notificationService.error('blog Created failed');
			} finally {
				setLoading(false);
			}
		} else {
			const formData = new FormData();
			formData.append('title', values.title);
			formData.append('heading', values.heading);
			formData.append('description', values.description);
			formData.append('date', values.date);
			formData.append('content', values.content);

			// Handle image file
			if (values.img && values.img instanceof File) {
				formData.append('img', values.img); // actual file
			} else if (typeof values.img === 'string') {
				formData.append('img_url', values.img); // just the URL
			}

			try {
				await updateBlog.mutateAsync([editBlog.id, formData]);
				resetForm();
				setToggle();
			} catch (error) {
				notificationService.error('blog updated failed');
			} finally {
				setLoading(false);
			}
		}
	};

	const handleCancel = () => {
		setToggle();
		setInitialValues({
			title: '',
			heading: '',
			description: '',
			date: '',
			content: '',
			img: null,
		});
	};

	const blogValidationSchema = Yup.object().shape({
		title: Yup.string().required('Title is required'),
		heading: Yup.string().required('Heading is required'),
		date: Yup.string().required('Date is required'),
		description: Yup.string().required('Description is required'),
		content: Yup.string().required('Content is required'),
		img: Yup.mixed().nullable().required('Image is required'),
	});

	useEffect(() => {
		if (editBlog) {
			setInitialValues({
				title: editBlog.title,
				heading: editBlog.heading,
				description: editBlog.description,
				date: editBlog.date,
				content: editBlog.content,
				img: editBlog?.img || null,
			});
		}
	}, [editBlog]);

	return (
		<div className="w-[100%] h-[100%]">
			{loading && (
				<div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30 flex-col gap-5">
					<Loader />
					<p className="text-[20px] font-semibold">Processing...</p>
				</div>
			)}
			<Formik
				initialValues={initialValues}
				onSubmit={handleSubmit}
				validationSchema={blogValidationSchema}
				enableReinitialize={true}
			>
				{({
					handleChange,
					setFieldValue,
					handleBlur,
					values,
					errors,
					touched,
				}) => (
					<Form>
						<div className="flex justify-between gap-5 mb-5">
							{/* Title Input */}
							<div className="w-2/5 flex flex-col gap-3">
								<label className="text-[20px] font-semibold">Title</label>
								<Input
									name="title"
									value={values.title}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="Enter title"
									className="py-[10px] h-[50px] text-[18px]"
								/>
								{touched.title && errors.title && (
									<div className="text-red-500">{errors.title}</div>
								)}
							</div>

							{/* Heading Input */}
							<div className="w-2/5 flex flex-col gap-3">
								<label className="text-[20px] font-semibold">Heading</label>
								<Input
									name="heading"
									value={values.heading}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="Enter heading"
									className="py-[10px] h-[50px] text-[18px]"
								/>
								{touched.heading && errors.heading && (
									<div className="text-red-500">{errors.heading}</div>
								)}
							</div>
							<div className="1/5 flex flex-col gap-3">
								<label className="text-[20px] font-semibold">Date</label>
								<DatePicker
									name="date"
									format="MMM DD YYYY" // display format
									value={values.date ? dayjs(values.date, 'YYYY-MM-DD') : null}
									onChange={(date) => {
										if (date && dayjs(date).isValid()) {
											// Store in YYYY-MM-DD for backend & consistency
											setFieldValue('date', date.format('YYYY-MM-DD'));
										} else {
											setFieldValue('date', '');
										}
									}}
									style={{ width: '100%' }}
									className="py-[10px] h-[50px] text-[16px]"
									allowClear
								/>
								{touched.date && errors.date && (
									<div className="text-red-500">{errors.date}</div>
								)}
							</div>
						</div>
						{/* Description Input */}
						<div className="flex flex-col gap-3">
							<label className="text-[20px] font-semibold">Description</label>
							<Input.TextArea
								name="description"
								value={values.description}
								onChange={handleChange}
								onBlur={handleBlur}
								placeholder="Enter description"
								rows={4}
								className="py-[10px] h-[50px] text-[18px]"
							/>
							{touched.description && errors.description && (
								<div className="text-red-500">{errors.description}</div>
							)}
						</div>

						{/* Editor */}

						<QuillFormik name={'content'} />
						{touched.content && errors.content && (
							<div className="text-red-500">{errors.content}</div>
						)}
						{/* Image Upload */}
						<div className="flex flex-col gap-3 mt-5">
							<label className="text-[20px] font-semibold">Image</label>
							<FileUploader mode="image-only" imageField="img" />
						</div>
						{touched.img && errors.img && (
							<div className="text-red-500">{errors.img}</div>
						)}

						{/* Submit Button */}
						<div className="mt-5 w-full flex justify-end pb-10 gap-5">
							<Button
								className="text-[16px] font-semibold"
								htmlType="button"
								onClick={handleCancel}
							>
								Cancel
							</Button>
							<Button
								color="default"
								variant="solid"
								className="text-[16px] font-semibold"
								htmlType="submit"
							>
								Submit
							</Button>
						</div>
					</Form>
				)}
			</Formik>
		</div>
	);
};
